// Today's schedule: an hour scale with the past shaded and a Now line; meetings and workshops as blocks at their
// time; reminders and timed due items as markers at their hour (due ones say how they stand); every untimed due
// item in one marker at the end. Everything opens the drawer.
import { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Reminder, TodayItem } from '@dpw/shared';
import { localTime } from '../../lib/format';
import { TYPE_CLS, TYPE_WORD, hourOf, inTime, placeBlocks, stripHours } from './util';
import type { DrawerState } from './Drawer';
import { useAssistantPanel } from '../../assistant/Panel';

interface Mark { key: string; h: number; at: string; text: string; cls: string; open: DrawerState }

let canvas: HTMLCanvasElement | null = null;
/** How wide a marker is: the dot, the time and the label, in the strip's own font. */
function markWidth(m: Mark, font: string) {
  canvas ??= document.createElement('canvas');
  const c = canvas.getContext('2d');
  if (!c) return 60 + 7 * (m.at.length + m.text.length);
  c.font = `500 12px ${font}`;
  const text = c.measureText(m.text).width;
  c.font = `400 10.5px ${font}`;
  return 10 + 6 + c.measureText(m.at).width + 6 + text + 10;
}

/**
 * Lay the markers out on three rows so no two labels run into each other: each goes on the first row where it
 * fits; near the right edge its label sits to the left of the dot. When no row has room, the label is shortened to
 * the space there is (it ends in an ellipsis).
 */
export const MARK_ROWS = 5;
/** The narrowest a meeting block gets, so its title and time can be read (CSS keeps the same min-width). */
const BLOCK_MIN = 150;
/** About how wide the "Now 23:55" label is (px): within half of it from an end, the label moves inside the strip. */
const NOW_LABEL = 76;

function layout(marks: Mark[], toPx: (h: number) => number, width: number, font: string) {
  const taken: [number, number][][] = Array.from({ length: MARK_ROWS }, () => []);
  const free = (row: [number, number][], a: number, b: number) => row.every(([x, y]) => b + 10 <= x || a >= y + 10);
  // The one marker for everything due with no set time goes first, so it is never the one shortened.
  return [...marks].sort((x, y) => Number(y.key === 'due-all') - Number(x.key === 'due-all') || x.h - y.h).map(m => {
    const x = toPx(m.h);
    const w = markWidth(m, font);
    const right = x - 5 + w > width;
    let [a, b] = right ? [x - w + 5, x + 5] : [x - 5, x - 5 + w];
    let row = taken.findIndex(r => free(r, a, b));
    let max: number | null = null;
    if (row < 0) {
      // No room: the row where the nearest label is furthest away, shortened to fit.
      // Room on the label's side of the dot in each row: up to the nearest label there (none if one covers the dot).
      const room = taken.map(r => Math.min(width, ...r.map(([s, y]) => (right ? (y <= x ? x - y - 10 : s < x + 5 ? 0 : Infinity) : (s >= x ? s - x - 10 : y > x - 5 ? 0 : Infinity)))));
      row = room.indexOf(Math.max(...room));
      max = Math.max(60, room[row]!);
      [a, b] = right ? [x - max, x + 5] : [x - 5, x + max];
    }
    taken[row]!.push([a, b]);
    return { ...m, row, right, max };
  });
}

export function Schedule({ meetings, due, reminders, now, onOpen }: {
  meetings: TodayItem[]; due: TodayItem[]; reminders: Reminder[]; now: Date; onOpen: (s: DrawerState) => void;
}) {
  const panel = useAssistantPanel();
  const nowT = localTime(now.toISOString());
  const nowH = hourOf(nowT);
  const timedRem = reminders.filter(r => r.remindAt && r.state !== 'done' && r.state !== 'overdue');
  const timedDue = due.filter(x => x.start);
  const untimed = due.filter(x => !x.start);
  const hs = [...meetings.flatMap(m => [hourOf(m.start!), hourOf(m.end ?? m.start!)]), ...timedRem.map(r => hourOf(localTime(r.remindAt!))), ...timedDue.map(x => hourOf(x.start!))];
  const { from, to, nowIn } = stripHours(hs, nowH, untimed.length > 0);
  const span = to - from;
  const pc = (h: number) => `${((h - from) / span) * 100}%`;
  const bad = untimed.filter(x => x.state.bad).length;
  const marks: Mark[] = [
    ...timedRem.map(r => ({ key: r.id, h: hourOf(localTime(r.remindAt!)), at: localTime(r.remindAt!), text: r.text,
      cls: r.setBy === 'assistant' ? 'ai' : r.state === 'late' ? 'late' : 'rem', open: { kind: 'reminder', id: r.id } as DrawerState })),
    ...timedDue.map(x => ({ key: x.id, h: hourOf(x.start!), at: x.start!, text: `${x.title} · ${x.state.label}`, cls: `dead ${x.state.bad ? 'bad' : 'ok'}`, open: { kind: 'item', id: x.id } as DrawerState })),
    ...(untimed.length ? [{ key: 'due-all', h: to - 0.3, at: 'No set time', text: `${untimed.length} due today${bad ? ` · ${bad} behind` : ''}`, cls: `dead grp ${bad ? 'bad' : 'ok'}`, open: { kind: 'due' } as DrawerState }] : []),
  ];
  const next = meetings.find(m => hourOf(m.start!) > nowH);
  const lateN = reminders.filter(r => r.state === 'late').length;
  const aiN = reminders.filter(r => r.setBy === 'assistant' && r.state !== 'done').length;
  const hours: number[] = [];
  for (let h = Math.ceil(from); h <= Math.floor(to); h++) hours.push(h);
  const tl = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1100);
  const [font, setFont] = useState('system-ui');
  useLayoutEffect(() => {
    const el = tl.current;
    if (!el) return;
    setFont(getComputedStyle(el).fontFamily);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const placed = layout(marks, h => ((h - from) / span) * width, width, font);
  const nowX = ((nowH - from) / span) * width;
  const used = Math.max(0, ...placed.map(m => m.row)) + 1;
  // Each block at its time, at least BLOCK_MIN wide, never over another or past the ends of the strip.
  const blocks = placeBlocks(meetings.map(m => ({ id: m.id, at: ((hourOf(m.start!) - from) / span) * width,
    w: (Math.max(0.5, hourOf(m.end ?? m.start!) - hourOf(m.start!)) / span) * width })), width, BLOCK_MIN);
  return (
    <section className="pnl sched" aria-label="Today's schedule">
      <div className="sech">
        <div className="sl"><b>Today&apos;s schedule</b>
          <span className="lg" aria-label="Legend"><i className="lm" />{meetings.length} {meetings.length === 1 ? 'meeting' : 'meetings'}<i className="ld" />{due.length} due<i className="lr" />{reminders.filter(r => r.state !== 'done').length} reminders
            {lateN > 0 && <><i className="ll" />late</>}{aiN > 0 && <><i className="la" />set by the assistant</>}</span></div>
        <div className="nextup">
          {next ? <>
            <span className="lb">Next</span><span className={`ty ${TYPE_CLS[next.type]}`}><i />{TYPE_WORD[next.type]}</span><b>{next.title}</b>
            <span className="xs mut">{next.start} · {inTime(next.start!, now)}</span>
            <button type="button" className="bt" onClick={() => panel.open({ activityId: next.id, spaceId: next.spaceId, label: next.title, prompt: `Brief me for ${next.title}`, send: true })}>Brief me</button>
          </> : <span className="xs mut">No more meetings today</span>}
          <Link className="bt ghost" to="/calendar">Open calendar</Link>
        </div>
      </div>
      <div className="tlwrap">
        <div className={`tl rows${used}`} ref={tl} data-from={from} data-to={to}>
          {nowIn && <div className="past" style={{ width: pc(nowH) }} />}
          {hours.map(h => <span key={h} className="hr" style={{ left: pc(h) }}><i>{String(h).padStart(2, '0')}:00</i></span>)}
          <div className="tlane tev">
            {meetings.map(m => {
              const { left, w } = blocks.get(m.id)!;
              return (
              <button key={m.id} type="button" className={`blk ${TYPE_CLS[m.type]}`} style={{ left, width: w }}
                onClick={() => onOpen({ kind: 'item', id: m.id })} aria-label={`${m.title}, ${m.start}${m.end ? ` to ${m.end}` : ''}`}>
                <b>{m.title}</b><span>{m.start}{m.end ? ` to ${m.end}` : ''} · {m.owner?.split(' ')[0] ?? m.trackName}{m.needsPrep && m.stepsTotal ? ` · prep ${m.stepsDone} of ${m.stepsTotal}` : ''}</span>
              </button>
              );
            })}
          </div>
          <div className="tlane tmk">
            {placed.map(m => (
              <button key={m.key} type="button" className={`mkr ${m.cls} r${m.row}${m.right ? ' rt' : ''}`} style={{ left: pc(m.h), maxWidth: m.max ?? undefined }} onClick={() => onOpen(m.open)} title={`${m.at} ${m.text}`}>
                <i /><em>{m.at}</em><span>{m.text}</span>
              </button>
            ))}
          </div>
          {/* Near an end of the strip (just before midnight, say) the label sits inside it instead of centred on the line. */}
          {nowIn && <div className={`nowl${nowX > width - NOW_LABEL / 2 ? ' end' : nowX < NOW_LABEL / 2 ? ' start' : ''}`} style={{ left: pc(nowH) }} aria-label="Now"><span>Now {nowT}</span></div>}
          {!meetings.length && !marks.length && <div className="tlempty">Nothing timed today.</div>}
        </div>
      </div>
    </section>
  );
}
